import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../book-create/login-validation.component/login-validation.component';

import { AlertService, AlertType } from '../../shared/services/alert.service';
import { BookVersionService } from '../../shared/services/apis/platform/book-version.service';
import { StatusVersionEnum } from '../../shared/services/enums/status-version.enum';
import { BookVersionResponse, BookVersionStruct } from '../../shared/services/responses/platform/book-version.response';
import { BookCreditsService } from '../../shared/services/apis/platform/book-credits.service';
import { RemainingCreditsResponse } from '../../shared/services/responses/platform/remaining-credits.response';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { BookReviewCreditAquisitionModalComponent } from './book-review-credit-aquisition-modal/book-review-credit-aquisition-modal.component';

import { BookPublishEditRequest } from '../../shared/services/requests/platform/book-publish-edit.request';
import { BookPublishEditService } from '../../shared/services/apis/platform/book-publish-edit.service';
import { BookPublishEditResponse } from '../../shared/services/responses/platform/book-publish-edit.response';

// Para checar pagamento pendente e reabrir o modal automaticamente
import { BookReviewCreditAcquisitionService } from '../../shared/services/apis/platform/book-review-credit-acquisition.service';
import { CreditAcquisitionResponse } from '../../shared/services/responses/platform/credit-acquisition.response';
import { RedirectService } from '../../redirect-service';
import { ReturnStruct } from '../../shared/services/structs/return.struct';

@Component({
  selector: 'app-book-approve',
  standalone: true,
  templateUrl: './book-approve.component.html',
  styleUrls: ['./book-approve.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MenuComponent,
    MatExpansionModule,
    FooterComponent,
    LoginValidationComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookApproveComponent implements OnInit {
  isLoading = false;
  idBookVersion!: number;

  @ViewChildren('imageInput') imageInputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('videoInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

  request: BookPublishEditRequest = {
    idBookVersion: 0,
    listKitStruct: [],
    listVideoChapterStruct: []
  };

  book: BookVersionStruct;
  bookTitle: string = '';
  remainingCredits: number = 0;
  bookCoverUrl: string = '';
  bookVersion?: any;
  videoFileName?: string;

  // controle para não abrir o modal pendente mais de uma vez
  private openedPendingModal = false;

  authorApprove = false; // se true, mostra mensagem de aprovação do autor

  // Enum de status (mesmos valores do backend)
  private BookReviewCreditPaymentStatus = {
    AguardandoPagamento: 1,
    PagamentoEmCurso: 2,
    Aprovado: 3,
    Reprovado: 4,
    Cancelado: 5
  } as const;

  constructor(
    private service: BookVersionService,
    private bookCreditsService: BookCreditsService,
    private creditService: BookReviewCreditAcquisitionService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private alert: AlertService,
    private redirectService: RedirectService,
    private bookPublishEditService: BookPublishEditService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onLoginValidated(logged: boolean) {
    if (!logged) return;
    this.GetBookVersion();
  }

  triggerFileInput(i: number, j: number) {
    const flatIndex = this.getFileInputIndex(i, j);
    const input = this.fileInputs.toArray()[flatIndex];
    input?.nativeElement?.click();
  }

  getFileInputIndex(i: number, j: number): number {
    let index = 0;
    for (let ci = 0; ci < this.request.listVideoChapterStruct.length; ci++) {
      const chapter = this.request.listVideoChapterStruct[ci];
      if (ci < i) {
        index += chapter.listVideoStruct.length;
      } else if (ci === i) {
        index += j;
        break;
      }
    }
    return index;
  }

  triggerImageInput(index: number): void {
    const inputRef = this.imageInputRefs.toArray()[index];
    inputRef?.nativeElement?.click();
  }

  GetBookVersion(): void {
    this.isLoading = true;
    this.service.GetBookVersion(this.idBookVersion, StatusVersionEnum.AguardandoAprovacaoDoAutor).subscribe({
      next: (res: BookVersionResponse) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription ?? 'Erro ao carregar dados.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.redirectService.redirectByStatus(res.bookVersionStruct.idStatusVersion, this.idBookVersion, res.bookVersionStruct.isCreditReviewing);

        this.authorApprove = res.bookVersionStruct.idStatusVersion === StatusVersionEnum.AguardandoAprovacaoDoAutor;

        this.book = res.bookVersionStruct;
        this.cdr.markForCheck();
        this.getBookCredits(true); // ao carregar créditos, checamos pendência
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar dados.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  getBookCredits(checkPendingAfterLoad = false): void {
    this.isLoading = true;
    this.bookCreditsService.GetCredits(this.idBookVersion).subscribe({
      next: (res: RemainingCreditsResponse) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription ?? 'Erro ao carregar dados.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        this.remainingCredits = res.remainingCredits;
        this.isLoading = false;
        this.cdr.markForCheck();

        if (checkPendingAfterLoad) {
          this.checkPendingAndOpen();
        }
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar dados.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** Se houver compra pendente (AguardandoPagamento ou PagamentoEmCurso), abre o modal automaticamente */
  private checkPendingAndOpen(): void {
    if (this.openedPendingModal) return;

    this.creditService.GetStatusCreditAquisition(this.idBookVersion, 0).subscribe({
      next: (res: CreditAcquisitionResponse) => {
        if (res?.isError) return;

        if (
          res.idCreditReviewPaymentStatus === this.BookReviewCreditPaymentStatus.AguardandoPagamento ||
          res.idCreditReviewPaymentStatus === this.BookReviewCreditPaymentStatus.PagamentoEmCurso
        ) {
          this.openedPendingModal = true;
          this.openCreditPurchaseModal();
        }
      },
      error: () => {
        // silencioso; não bloqueia a página
      }
    });
  }

  /** Abre o modal de aquisição de crédito */
  private openCreditPurchaseModal(): void {
    const ref = this.dialog.open(BookReviewCreditAquisitionModalComponent, {
      width: '760px',
      maxWidth: '95vw',
      disableClose: true, // pagamento só via modal
      autoFocus: false,
      restoreFocus: false,
      data: {
        idBookVersion: this.idBookVersion,
        priceLabel: 'R$ 199,90',
        priceValue: 199.90,
        onCompleted: ({ success, error }: { success: boolean; error?: string }) => {
          this.getBookCredits();
          if (!success && error) {
            this.alert.show('Pagamento', error, AlertType.warning);
          }
        }
      }
    });

    ref.afterClosed().subscribe(() => {
      this.getBookCredits();
      this.openedPendingModal = false; // libera para futuras aberturas automáticas
      this.cdr.markForCheck();
    });
  }

  useCredits(): void {
    this.isLoading = true;

    // Sem créditos: oferece compra e abre modal somente se confirmar
    if ((this.remainingCredits ?? 0) <= 0) {
      this.isLoading = false; // desligar antes de abrir o modal de confirmação
      const noCreditRef = this.dialog.open(ConfirmModalComponent, {
        data: {
          title: 'Sem créditos',
          description: 'Você não possui créditos para solicitar nova revisão.',
          buttonConfirm: 'Comprar créditos',
          buttonClose: 'Fechar'
        }
      });

      noCreditRef.afterClosed().subscribe(r => {
        if (r && r.confirm) {
          this.openCreditPurchaseModal();
        }
      });

      return;
    }

    // Com crédito: confirma utilização
    this.isLoading = false; // não manter spinner enquanto espera confirmação
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Usar crédito',
        description: `Ao confirmar, seu livro volta para edição e você precisará submetê-lo à revisão.`,
        buttonConfirm: 'Sim, usar crédito',
        buttonClose: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.isLoading = true;
        this.bookCreditsService.UseCredits(this.idBookVersion).subscribe({
          next: (res: RemainingCreditsResponse) => {
            if (res.isError) {
              this.alert.show('Erro', res.errorDescription ?? 'Erro ao carregar dados.', AlertType.error);
              this.isLoading = false;
              this.cdr.markForCheck();
              return;
            }

            this.remainingCredits = res.remainingCredits;
            this.cdr.markForCheck();
            // redireciona para edição do livro (nova revisão)
            this.router.navigate(['/novo-livro', this.idBookVersion]);
          },
          error: () => {
            this.alert.show('Erro', 'Erro inesperado ao carregar dados.', AlertType.error);
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      } else {
        // cancelado pelo usuário
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  save(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Publicar livro',
        description: `Ao confirmar, seu livro será publicado e não terá mais acesso a revisão.`,
        buttonConfirm: 'Sim, publicar',
        buttonClose: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.bookPublishEditService.PublishBook(this.idBookVersion).subscribe({
          next: (res: ReturnStruct) => {
            if (res.isError) {
              this.alert.show('Erro', res.errorDescription ?? 'Erro ao publicar livro.', AlertType.error);
              this.cdr.markForCheck();
              return;
            }
            this.router.navigate(['/editar-publicacao', this.idBookVersion]);
          }
        });
      }
    });
  }

  openPdf(): void {
    if (this.book?.bookReviewedFileName) {
      window.open(this.book.bookReviewedFileName, '_blank');
    }
  }
}
